import { detalheCustoAquisicaoSchema, type CustoAquisicaoResponse, type FichaResponse, type PrecoPraticadoItemResponse } from '@triade/contracts';
import { ehZero, formatarDataHora, formatarTaxa, formatarCusto, formatarMoeda, formatarData, formatarQuantidade } from '@/lib/format';
import { ehComprado } from '@triade/contracts';
import type { TipoItem } from './tipos';

const ROTULO_CREDITO: Record<string, string> = { ICMS: 'ICMS', PIS: 'PIS', COFINS: 'COFINS' };
const ROTULO_ACRESCIMO: Record<string, string> = { IPI: 'IPI', FREIGHT: 'Frete', INSURANCE: 'Seguro', ICMS_DIF: 'Diferencial de ICMS', ICMS_ST: 'ICMS-ST' };
const ROTULO_IPI: Record<CustoAquisicaoResponse['ipiTratamento'], string> = {
  NENHUM: 'sem IPI',
  CUSTO: 'IPI entra no custo',
  RECUPERAVEL: 'IPI recuperável (fora do custo)',
};

function Secao({ titulo, children, rotulo }: { titulo: string; children: React.ReactNode; rotulo: string }) {
  return (
    <section aria-label={rotulo} className="rounded-xl border border-border p-4">
      <h3 className="text-body font-semibold">{titulo}</h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

const Mudo = ({ children, papel = 'status' }: { children: React.ReactNode; papel?: 'status' }) => (
  <p role={papel} className="text-small text-ink-muted">
    {children}
  </p>
);

/** Link para a tela Ficha tecnica (entrada por ?itemId=, abre a versao escolhida). */
export const ROTA_FICHA = '/custos/fichas-tecnicas';

/**
 * Componentes de custo para mostrar na aba de precificacao (Sessao 37): so as etapas com minutos > 0 (nada zerado); os demais componentes ficam
 * como estao. Etapa sem descricao (observacao) mostra o nome do centro analitico.
 */
export function componentesVisiveis(componentes: FichaResponse['componentes']): Array<NonNullable<FichaResponse['componentes']>[number] & { rotulo: string }> {
  return (componentes ?? [])
    .filter((c) => !c.centroCustoId || !ehZero(c.quantidade))
    .map((c) => ({ ...c, rotulo: c.descricao.trim() !== '' ? c.descricao : c.centroCustoId ? (c.centroCustoNome ?? c.centroCustoCodigo ?? 'Etapa') : c.descricao }));
}

export function FichaEscolhida({ ficha, itemId, tipo }: { ficha: FichaResponse | null; itemId: string; tipo: TipoItem }) {
  // Sem custo.ver (PCP) a API manda so a estrutura: sem colunas de R$.
  const comValores = ficha?.custoUnitario !== undefined;
  const componentes = componentesVisiveis(ficha?.componentes);
  return (
    <Secao titulo="Ficha técnica escolhida" rotulo="Ficha técnica">
      {ficha === null ? (
        <Mudo>
          {tipo === 'SERVICO'
            ? 'Sem ficha escolhida. A composição do serviço (horas e custo por hora) vem da ficha técnica escolhida.'
            : 'Sem ficha escolhida. O custo do produto fabricado vem da ficha técnica escolhida; sem ela o item não pode ser precificado.'}
        </Mudo>
      ) : (
        <div data-testid="ficha-escolhida">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-semibold">Versão {ficha.versao}</span>
            {ficha.descricao && <span className="text-ink-secondary">{ficha.descricao}</span>}
            {ficha.custoUnitario !== undefined && <span className="tabular ml-auto text-metric font-semibold">{formatarCusto(ficha.custoUnitario)}</span>}
          </div>
          <div className="text-caption text-ink-muted">
            {ficha.custoUnitario !== undefined && ficha.custoCalculadoEm !== undefined
              ? `Custo unitário (soma de ${componentes.length} componentes) calculado em ${formatarDataHora(ficha.custoCalculadoEm)}`
              : `${componentes.length} componentes (só a estrutura, sem valores)`}
          </div>
          {componentes.length > 0 && (
            <table className="mt-2 w-full border-collapse text-small">
              <thead>
                <tr className="text-left text-caption text-ink-muted">
                  <th scope="col" className="py-1.5 pr-3 font-medium">Componente</th>
                  <th scope="col" className="px-3 py-1.5 text-right font-medium">Quantidade</th>
                  {comValores && <th scope="col" className="px-3 py-1.5 text-right font-medium">Custo unit.</th>}
                  {comValores && <th scope="col" className="py-1.5 pl-3 text-right font-medium">Custo total</th>}
                </tr>
              </thead>
              <tbody>
                {componentes.map((c) => (
                  <tr key={c.id} className="border-t border-line-soft">
                    <td className="py-2 pr-3">{c.rotulo}</td>
                    <td className="tabular px-3 py-2 text-right">
                      {formatarQuantidade(c.quantidade)} {c.unidade ?? ''}
                    </td>
                    {comValores && <td className="tabular px-3 py-2 text-right">{formatarCusto(c.custoUnitario ?? '0')}</td>}
                    {comValores && <td className="tabular py-2 pl-3 text-right">{formatarCusto(c.custoTotal ?? '0')}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <a href={`${ROTA_FICHA}?itemId=${encodeURIComponent(itemId)}`} className="mt-3 inline-block text-small font-medium text-primary hover:underline">
        Abrir na tela Ficha técnica
      </a>
    </Secao>
  );
}

function Linha({ rotulo, valor, destaque = false }: { rotulo: React.ReactNode; valor: string; destaque?: boolean }) {
  return (
    <tr className="border-t border-line-soft">
      <td className={destaque ? 'py-2 pr-3 font-semibold' : 'py-2 pr-3'}>{rotulo}</td>
      <td className={`tabular py-2 pl-3 text-right ${destaque ? 'font-semibold' : ''}`}>{valor}</td>
    </tr>
  );
}

/** Composicao do custo de aquisicao como a API gravou (saida do motor): creditos, acrescimos e custo. Nada e recalculado. */
export function ComposicaoCusto({ custo }: { custo: CustoAquisicaoResponse }) {
  // tributos com taxa gravada e switch desligado: nao creditam, ficam dentro do custo
  const semCredito = [
    custo.switchIcms || ehZero(custo.creditIcms) ? null : 'ICMS',
    custo.switchPis || ehZero(custo.creditPis) ? null : 'PIS',
    custo.switchCofins || ehZero(custo.creditCofins) ? null : 'COFINS',
  ].filter((t): t is string => t !== null);
  const detalhe = detalheCustoAquisicaoSchema.safeParse(custo.detalhe);
  const d = detalhe.success ? detalhe.data : null;
  return (
    <div data-testid="custo-aquisicao">
      <div className="text-caption text-ink-muted">
        Vigente desde {formatarData(custo.validFrom)} · {ROTULO_IPI[custo.ipiTratamento]}
        {custo.ipiTratamento !== 'NENHUM' ? ` (${formatarTaxa(custo.ipiRate)})` : ''}
        {semCredito.length > 0 ? ` · sem crédito (switch desligado): ${semCredito.join(', ')}` : ''} · motor {custo.motorVersao}
      </div>
      <table className="mt-2 w-full border-collapse text-small">
        <tbody>
          <Linha rotulo="Preço base" valor={formatarCusto(custo.basePrice)} />
          {d?.credits.map((c) => (
            <Linha key={`c-${c.tax}`} rotulo={`(−) Crédito de ${ROTULO_CREDITO[c.tax] ?? c.tax} · ${formatarTaxa(c.rate)}`} valor={formatarCusto(c.value)} />
          ))}
          {d?.additions.map((a) => (
            <Linha key={`a-${a.kind}`} rotulo={`(+) ${ROTULO_ACRESCIMO[a.kind] ?? a.kind} · ${formatarTaxa(a.rate)}`} valor={formatarCusto(a.value)} />
          ))}
          <Linha rotulo="Crédito tributário total" valor={formatarCusto(custo.acquisitionTaxCredit)} />
          <Linha rotulo="Custo de aquisição" valor={formatarCusto(custo.acquisitionCost)} destaque />
        </tbody>
      </table>
      {!d && <Mudo papel="status">O detalhe da composição não veio no formato esperado; os totais acima são os gravados.</Mudo>}
    </div>
  );
}

export function CustoAquisicaoVigente({ tipo, custo }: { tipo: TipoItem; custo: CustoAquisicaoResponse | null }) {
  return (
    <Secao titulo="Custo de aquisição vigente" rotulo="Custo de aquisição">
      {!ehComprado(tipo) ? (
        <Mudo>Este tipo não tem custo de aquisição. O custo vem da ficha técnica.</Mudo>
      ) : custo === null ? (
        <Mudo>Sem custo de aquisição vigente hoje: o item fica sem custo (aparece como “sem custo” na lista) e não pode ser precificado.</Mudo>
      ) : (
        <ComposicaoCusto custo={custo} />
      )}
    </Secao>
  );
}

export function PrecosPraticados({ precos }: { precos: PrecoPraticadoItemResponse[] }) {
  return (
    <Secao titulo="Preços praticados recentes" rotulo="Preços praticados">
      {precos.length === 0 ? (
        <Mudo>Nenhum preço praticado registrado para este item.</Mudo>
      ) : (
        <table data-testid="precos-praticados" className="w-full border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="py-1.5 pr-3 font-medium">Tabela</th>
              <th scope="col" className="px-3 py-1.5 font-medium">Condição</th>
              <th scope="col" className="px-3 py-1.5 text-right font-medium">Preço</th>
              <th scope="col" className="py-1.5 pl-3 text-right font-medium">Registrado em</th>
            </tr>
          </thead>
          <tbody>
            {precos.map((p) => (
              <tr key={p.id} className="border-t border-line-soft">
                <td className="py-2 pr-3">{p.tabelaNome}</td>
                <td className="px-3 py-2 text-ink-secondary">{p.condicaoNome}</td>
                <td className="tabular px-3 py-2 text-right font-semibold">{formatarMoeda(p.price)}</td>
                <td className="tabular py-2 pl-3 text-right text-ink-secondary">{formatarDataHora(p.recordedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Secao>
  );
}

